/**
 * Intersection Observer 图片懒加载优化
 * 功能：
 * 1. 使用 Intersection Observer 实现高性能图片懒加载
 * 2. 支持响应式图片 srcset
 * 3. 支持背景图片懒加载
 * 4. 预加载即将进入视口的图片
 * 5. 低质量图片占位符 (LQIP) 支持
 * 6. 原生 loading="lazy" 降级支持
 */

(function() {
  'use strict';

// 配置选项
  const CONFIG = {
    rootMargin: '50px 0px 100px 0px',
    preloadMargin: '200px 0px 400px 0px',
    threshold: 0,
    placeholderColor: '#f0f0f0',
    fadeInEffect: true,
    fadeInDuration: 300
  };
  const isIntersectionObserverSupported = 'IntersectionObserver' in window;
  let mainObserver = null;
  let preloadObserver = null;
  let loadedImages = new Set();
  // ==========================================
  // 初始化
  // ==========================================
  function init() {
    if (!isIntersectionObserverSupported) {
      loadAllImagesImmediately();
      return;
    }
    mainObserver = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: CONFIG.rootMargin,
      threshold: CONFIG.threshold
    });
    preloadObserver = new IntersectionObserver(handlePreload, {
      root: null,
      rootMargin: CONFIG.preloadMargin,
      threshold: 0
    });
    setupLazyImages();
    setupBackgroundImages();
    observeDynamicContent();
  }
  // ==========================================
  // 主观察器回调
  // ==========================================
  function handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (loadedImages.has(img)) return;
        loadedImages.add(img);
        loadImage(img);
        mainObserver.unobserve(img);
        if (preloadObserver) preloadObserver.unobserve(img);
      }
    });
  }

  // ==========================================
  // 预加载观察器回调
  // ==========================================
  function handlePreload(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (!loadedImages.has(img) && !img.classList.contains('is-preloading')) {
          img.classList.add('is-preloading');
          preloadImage(img);
        }
        preloadObserver.unobserve(img);
      }
    });
  }
  // ==========================================
  // 处理单张图片（用于初始化 + MutationObserver）
  // ==========================================
  function processImage(img) {
    if (loadedImages.has(img)) return;
    setupPlaceholder(img);
    if (!img.dataset.src && img.src) {
      img.dataset.src = img.src;
      img.removeAttribute('src');
    }
    if (!img.hasAttribute('loading')) {
      img.setAttribute('loading', 'lazy');
    }
    mainObserver.observe(img);
    if (preloadObserver) preloadObserver.observe(img);
  }
  // ==========================================
  // 处理单个背景元素
  // ==========================================
  function processBackground(el) {
    if (loadedImages.has(el)) return;
    el.style.cssText += ';background-color:' + CONFIG.placeholderColor + ';';
    mainObserver.observe(el);
  }
  // ==========================================
  // 设置懒加载图片
  // ==========================================
  function setupLazyImages() {
    const lazyImages = document.querySelectorAll('img[data-src], img[loading="lazy"]');
    lazyImages.forEach(processImage);
  }
  
  // ==========================================
  // 设置背景图片懒加载
  // ==========================================
  function setupBackgroundImages() {
    const lazyBackgrounds = document.querySelectorAll('[data-bg-src]');
    
    lazyBackgrounds.forEach(processBackground);
  }
  // ==========================================
  // 设置占位符
  // ==========================================
  function setupPlaceholder(img) {
    let cssText = '';
    if (img.width && img.height) {
      cssText += 'aspect-ratio:' + img.width + ' / ' + img.height + ';';
      if (CONFIG.fadeInEffect && !img.classList.contains('lazy-fade')) {
        img.classList.add('lazy-fade');
        cssText += 'opacity:0;transition:opacity ' + CONFIG.fadeInDuration + 'ms ease-in-out;';
      }
    }
    if (cssText) {
      img.style.cssText += ';' + cssText;
    }
    if (!img.hasAttribute('alt')) {
      img.setAttribute('alt', '');
    }
  }

  // ==========================================
  // 加载图片
  // ==========================================
   function loadImage(img) {
    const src = img.dataset.src;
    const srcset = img.dataset.srcset;
    if (!src) return;
    const preloadImg = new Image();
    preloadImg.onload = function() {
      img.src = src;
      if (srcset) img.srcset = srcset;
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      if (CONFIG.fadeInEffect) {
        requestAnimationFrame(() => {
          img.style.opacity = '1';
          // 淡入完成后清理 aspect-ratio
          setTimeout(() => {
            img.style.aspectRatio = '';
          }, CONFIG.fadeInDuration);
        });
      } else {
        img.style.aspectRatio = '';
      }
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-sizes');
      img.classList.add('is-loaded');
      img.classList.remove('is-preloading');
      img.dispatchEvent(new CustomEvent('lazyloaded', { detail: { src: src } }));
    };
    preloadImg.onerror = function() {
      console.warn('[LazyLoad] Failed to load:', src);
      img.classList.add('is-error');
      img.dispatchEvent(new CustomEvent('lazyerror', { detail: { src: src } }));
    };
    preloadImg.src = src;
  }

  // ==========================================
  // 预加载图片（低优先级）
  // ==========================================
  function preloadImage(img) {
    const src = img.dataset.src;
    if (!src) return;
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        const link = document.createElement('link');
      }, { timeout: 2000 });
    }
  }

  // ==========================================
  // 加载背景图片
  // ==========================================
  function loadBackgroundImage(el) {
    const bgSrc = el.dataset.bgSrc;
      if (!bgSrc) return;
    const img = new Image();
    img.onload = function() {
      el.style.backgroundImage = 'url(' + bgSrc + ')';
      el.classList.add('bg-is-loaded');
      el.removeAttribute('data-bg-src');
      if (CONFIG.fadeInEffect) {
        el.style.cssText += ';opacity:0;transition:opacity ' + CONFIG.fadeInDuration + 'ms ease-in-out;';
        requestAnimationFrame(() => {
          el.style.opacity = '1';
        });
      }
    };
    img.src = bgSrc;
  }


  // ==========================================
  // 降级：立即加载所有图片
  // ==========================================
  function loadAllImagesImmediately() {
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      }
    });
  }
  // ==========================================
  // 监听动态添加的内容
  // ==========================================
  function observeDynamicContent() {
    let pendingNodes = [];
    let timeoutId = null;
    const mutationObserver = new MutationObserver(function(mutations) {
      let hasNew = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            pendingNodes.push(node);
            hasNew = true;
          }
        });
      });
      if (hasNew) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(processPendingNodes, 150);
      }
    });


    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    function processPendingNodes() {
      const images = [];
      const backgrounds = [];
      pendingNodes.forEach(function(node) {
        if (node.matches && node.matches('img[data-src]')) images.push(node);
        if (node.matches && node.matches('img[loading="lazy"]')) images.push(node);
        if (node.matches && node.matches('[data-bg-src]')) backgrounds.push(node);
        if (node.querySelectorAll) {
          node.querySelectorAll('img[data-src], img[loading="lazy"]').forEach(img => images.push(img));
          node.querySelectorAll('[data-bg-src]').forEach(el => backgrounds.push(el));
        }
      });
      images.forEach(processImage);
      backgrounds.forEach(processBackground);
      pendingNodes = [];
    }
  }
  // ==========================================
  // 公共 API
  // ==========================================
  window.LazyLoadObserver = {
    refresh: function() {
      setupLazyImages();
      setupBackgroundImages();
    },
    load: function(selector) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el.tagName === 'IMG') loadImage(el);
        else if (el.dataset.bgSrc) loadBackgroundImage(el);
      });
    },
    config: CONFIG
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();